import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { AuthHttpInterceptor } from '@auth0/auth0-angular';
import type { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { StorageKeys, StorageService } from '../../services/storage.service';
import { AuthContextService } from '../services/auth-context.service';
import { AuthOperationsService } from '../services/auth-operations.service';
import { isAccountNotProvisioned, readErrorDetail } from '../login-problems';
import { CookieAuthInterceptor } from './cookie-auth.interceptor';

@Injectable()
export class UnifiedAuthInterceptor implements HttpInterceptor {
	private injector = inject(Injector);
	private storageService = inject(StorageService);
	private authContextService = inject(AuthContextService);
	private authOperations = inject(AuthOperationsService);

	private cookieAuthInterceptor?: CookieAuthInterceptor;
	private authInterceptor?: AuthHttpInterceptor;

	/**
	 * URLs where 401 errors should trigger automatic logout.
	 */
	private urlsToLogOutIfUnauthorizedError = ['v1/parent/@me', 'v1/users/@me', 'v1/schools', 'v1/locations', 'v1/pinnables/arranged', 'v2/users/Init'];

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		// Determine which authentication scheme to use
		const scheme = this.authContextService.authenticationScheme();

		// Route to appropriate authentication interceptor
		let authHandler$: Observable<HttpEvent<unknown>>;

		if (scheme === 'auth0') {
			this.authInterceptor ??= this.injector.get(AuthHttpInterceptor);
			const kioskAwareRequest = this.needsKioskCredentials(request.url) ? request.clone({ withCredentials: true }) : request;
			authHandler$ = this.authInterceptor.intercept(kioskAwareRequest, next);
		} else {
			this.cookieAuthInterceptor ??= this.injector.get(CookieAuthInterceptor);
			// Default to cookie auth for backwards compatibility
			authHandler$ = this.cookieAuthInterceptor.intercept(request, next);
		}

		// Apply common error handling and response processing
		return authHandler$.pipe(
			catchError((error) => {
				// Log out user if 401 error occurs on critical authentication routes.
				// An account that is still being provisioned is not an expired session: logging out would
				// silently sign straight back in and loop, so UserInitService waits and retries instead.
				if (
					error instanceof HttpErrorResponse &&
					this.shouldLogOutOnError(request.url) &&
					error.status === 401 &&
					!isAccountNotProvisioned(error, scheme)
				) {
					this.authOperations.sessionExpiredLogout(readErrorDetail(error));
				}
				return throwError(error);
			}),
			tap((response) => {
				// Store kiosk mode location if header is present
				if (response?.type === HttpEventType.Response) {
					const kioskLocation = response.headers.get('X-Kiosk-Mode-Location-Id');
					if (kioskLocation) {
						this.storageService.setItem(StorageKeys.kioskModeLocationId, kioskLocation);
					}
				}
			})
		);
	}

	private needsKioskCredentials(url: string): boolean {
		if (url.includes('sessions/enter-kiosk-mode')) {
			return true;
		}
		return !!this.storageService.getItem(StorageKeys.kioskModeLocationId);
	}

	/**
	 * Determines if a 401 error on the given URL should trigger automatic logout.
	 */
	private shouldLogOutOnError(url: string): boolean {
		return this.urlsToLogOutIfUnauthorizedError.some((urlFragment) => url.includes(urlFragment));
	}
}
